import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiBaseUrl } from '../api/client'

// ---- Types ----

export type InterventionKind = 'preventive' | 'corrective' | 'works'
export type InterventionStatus =
  // `planned` (spec 2026-08-24-01) is the preventive scheduler's output: an
  // occurrence generated ahead of its date that nobody has ordered yet. It
  // precedes `requested`, which is what it becomes when the scheduler arms it.
  | 'planned'
  | 'requested'
  | 'scheduled'
  | 'assigned'
  | 'in_progress'
  | 'done'
  | 'cancelled'
export type BillingStatus = 'included' | 'to_bill' | 'billed' | 'not_applicable'

export interface InterventionDocument {
  document_uid: string
  created_at: string
  document?: { uid: string; name?: string; file_name?: string; file_mime_type?: string } | null
}

export type InterventionEventType =
  | 'created'
  | 'status_changed'
  | 'rescheduled'
  | 'provider_assigned'
  | 'document_attached'
  | 'document_detached'
  | 'expense_linked'
  | 'comment'
  // Work-order loop (spec 2026-08-01-01)
  | 'os_sent'
  | 'os_accepted'
  | 'os_declined'
  | 'date_proposed'
  | 'work_submitted'
  | 'work_validated'
  | 'work_disputed'
  | 'equipment_selected'
  | 'equipment_declared'
  | 'equipment_confirmed'
  | 'equipment_rejected'
  | 'equipment_merged'
  // Preventive schedule (spec 2026-08-24-01)
  | 'schedule_generated'
  | 'schedule_armed'
  | 'schedule_skipped'

export interface InterventionEvent {
  uid: string
  event_type: InterventionEventType
  payload?: Record<string, unknown> | null
  user_uid?: string | null
  user?: { uid: string; name?: string } | null
  created_at: string
}

export interface Expense {
  uid: string
  asset_uid: string
  intervention_uid?: string | null
  contract_uid?: string | null
  capex_worksite_uid?: string | null
  document_uid?: string | null
  supplier_user_uid?: string | null
  label: string
  amount_without_tax?: number | null
  tax_amount?: number | null
  amount: number
  currency: string
  expense_date?: string | null
  created_at: string
  updated_at: string
}

export interface Intervention {
  uid: string
  reference: string
  asset_uid: string
  kind: InterventionKind
  category?: string
  status: InterventionStatus
  title: string
  description?: string
  scheduled_at?: string | null
  performed_at?: string | null
  cost_amount?: number | null
  cost_currency?: string | null
  provider_user_uid?: string | null
  contract_uid?: string | null
  issue_uid?: string | null
  capex_worksite_uid?: string | null
  previous_intervention_uid?: string | null
  billable: boolean
  billing_status: BillingStatus
  order_ref?: string
  // --- Work-order cycle (spec 2026-08-01-01) ---
  os_sent_at?: string | null
  os_responded_at?: string | null
  os_response?: 'accepted' | 'declined' | null
  work_submitted_at?: string | null
  validated_at?: string | null
  validated_by_user_uid?: string | null
  validation_status?: 'approved' | 'disputed' | null
  /**
   * Free-form entity properties. Carries `os_email`, the delivery state of the
   * ordre de service e-mail (spec 2026-08-03-01) — see EmailDeliveryChip.
   */
  properties?: Record<string, unknown>
  /**
   * Audience (spec 2026-09-04-03, decision 7): `internal` or `shared`. NULL
   * means `internal` — an intervention is a manager's row until someone says
   * otherwise.
   */
  visibility?: 'internal' | 'shared' | null
  /** What the caller may do on THIS row, computed from its scope columns. */
  permissions?: string[]
  documents?: InterventionDocument[]
  provider?: { uid: string; name?: string } | null
  asset?: { uid: string; name?: string } | null
  created_at: string
  updated_at: string
}

interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
  total_pages: number
}

export interface InterventionFilters {
  /** Page size; 200 by default. The 24-month calendar asks for more. */
  limit?: number
  kind?: string // CSV
  status?: string // CSV
  contract_uid?: string
  subtree?: boolean
}

export interface CreateInterventionInput {
  asset_uid: string
  kind: InterventionKind
  status?: InterventionStatus
  title: string
  description?: string
  category?: string
  scheduled_at?: string | null
  performed_at?: string | null
  cost_amount?: number | null
  cost_currency?: string | null
  provider_user_uid?: string | null
  contract_uid?: string | null
  previous_intervention_uid?: string | null
  order_ref?: string
  billable?: boolean
}

export type UpdateInterventionInput = Partial<
  Omit<CreateInterventionInput, 'asset_uid'>
> & { billing_status?: BillingStatus; visibility?: 'internal' | 'shared' }

// ---- Fetch helper (mirrors useComplianceApi) ----

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: { ...getAuthHeaders(), ...options?.headers },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw { status: res.status, ...body }
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

const interventionsKey = (org: string, assetUid?: string) =>
  ['interventions', org, assetUid ?? 'all'] as const

// ---- Hooks ----

export function useInterventions(
  org: string,
  assetUid: string | undefined,
  filters: InterventionFilters = {},
) {
  return useQuery({
    queryKey: [...interventionsKey(org, assetUid), filters],
    queryFn: () => {
      const p = new URLSearchParams({ limit: String(filters.limit ?? 200) })
      if (assetUid) {
        p.set('asset_uid', assetUid)
        if (filters.subtree !== false) p.set('subtree', 'true')
      }
      if (filters.kind) p.set('kind', filters.kind)
      if (filters.status) p.set('status', filters.status)
      if (filters.contract_uid) p.set('contract_uid', filters.contract_uid)
      return apiFetch<PaginatedResponse<Intervention>>(
        `/api/v1/organizations/${org}/interventions?${p.toString()}`,
      )
    },
    enabled: !!org,
    select: (d) => d.data ?? [],
  })
}

export function useCreateIntervention(org: string, assetUid?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateInterventionInput) =>
      apiFetch<Intervention>(`/api/v1/organizations/${org}/interventions`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: interventionsKey(org, assetUid) }),
  })
}

// Invalidates every cached `useInterventions` list variant for this org,
// regardless of the assetUid/filters suffix baked into the query key —
// covers the org-wide list page, the asset-tab list, and any filtered view.
function invalidateAllInterventionLists(qc: ReturnType<typeof useQueryClient>, org: string) {
  qc.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey as unknown[]
      return key[0] === 'interventions' && key[1] === org
    },
  })
}

export function useUpdateIntervention(org: string, assetUid?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uid, ...input }: UpdateInterventionInput & { uid: string }) =>
      apiFetch<Intervention>(
        `/api/v1/organizations/${org}/interventions/${uid}`,
        { method: 'PATCH', body: JSON.stringify(input) },
      ),
    onSuccess: (_data, { uid }) => {
      invalidateAllInterventionLists(qc, org)
      qc.invalidateQueries({ queryKey: interventionDetailKey(org, uid) })
      qc.invalidateQueries({ queryKey: interventionEventsKey(org, uid) })
      if (assetUid) qc.invalidateQueries({ queryKey: interventionsKey(org, assetUid) })
    },
  })
}

export function useDeleteIntervention(org: string, assetUid?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uid: string) =>
      apiFetch<void>(`/api/v1/organizations/${org}/interventions/${uid}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, uid) => {
      invalidateAllInterventionLists(qc, org)
      qc.invalidateQueries({ queryKey: interventionDetailKey(org, uid) })
      if (assetUid) qc.invalidateQueries({ queryKey: interventionsKey(org, assetUid) })
    },
  })
}

// ---- Detail: single intervention ----

const interventionDetailKey = (org: string, uid: string) =>
  ['intervention-detail', org, uid] as const

export function useIntervention(org: string, uid: string | undefined) {
  return useQuery({
    queryKey: interventionDetailKey(org, uid ?? 'none'),
    queryFn: () =>
      apiFetch<Intervention>(
        `/api/v1/organizations/${org}/interventions/${uid}`,
      ),
    enabled: !!org && !!uid,
  })
}

// ---- Documents (§1) ----

const interventionDocsKey = (org: string, uid: string) =>
  ['intervention-documents', org, uid] as const

export function useInterventionDocuments(org: string, uid: string | undefined) {
  return useQuery({
    queryKey: interventionDocsKey(org, uid ?? 'none'),
    queryFn: () =>
      apiFetch<{ data: InterventionDocument[] }>(
        `/api/v1/organizations/${org}/interventions/${uid}/documents`,
      ),
    enabled: !!org && !!uid,
    select: (d) => d.data ?? [],
  })
}

export function useAttachDocuments(org: string, uid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (documentUids: string[]) =>
      apiFetch<{ data: InterventionDocument[] }>(
        `/api/v1/organizations/${org}/interventions/${uid}/documents`,
        { method: 'POST', body: JSON.stringify({ document_uids: documentUids }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: interventionDocsKey(org, uid) })
      qc.invalidateQueries({ queryKey: interventionDetailKey(org, uid) })
      qc.invalidateQueries({ queryKey: interventionEventsKey(org, uid) })
    },
  })
}

export function useDetachDocument(org: string, uid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (documentUid: string) =>
      apiFetch<void>(
        `/api/v1/organizations/${org}/interventions/${uid}/documents/${documentUid}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: interventionDocsKey(org, uid) })
      qc.invalidateQueries({ queryKey: interventionDetailKey(org, uid) })
      qc.invalidateQueries({ queryKey: interventionEventsKey(org, uid) })
    },
  })
}

// ---- Events timeline (§2) ----

const interventionEventsKey = (org: string, uid: string) =>
  ['intervention-events', org, uid] as const

export function useInterventionEvents(org: string, uid: string | undefined) {
  return useQuery({
    queryKey: interventionEventsKey(org, uid ?? 'none'),
    queryFn: () =>
      apiFetch<PaginatedResponse<InterventionEvent>>(
        `/api/v1/organizations/${org}/interventions/${uid}/events?limit=100`,
      ),
    enabled: !!org && !!uid,
    select: (d) => d.data ?? [],
  })
}

// ---- Expenses (§7) ----

const expensesKey = (org: string, interventionUid?: string) =>
  ['expenses', org, interventionUid ?? 'all'] as const

export interface CreateExpenseInput {
  asset_uid?: string
  intervention_uid?: string
  contract_uid?: string | null
  document_uid?: string | null
  supplier_user_uid?: string | null
  label: string
  amount_without_tax?: number | null
  tax_amount?: number | null
  amount: number
  currency?: string
  expense_date?: string | null
}

export function useExpenses(org: string, interventionUid: string | undefined) {
  return useQuery({
    queryKey: expensesKey(org, interventionUid),
    queryFn: () =>
      apiFetch<PaginatedResponse<Expense>>(
        `/api/v1/organizations/${org}/expenses?intervention_uid=${interventionUid}&limit=200`,
      ),
    enabled: !!org && !!interventionUid,
    select: (d) => d.data ?? [],
  })
}

export function useCreateExpense(org: string, interventionUid?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateExpenseInput) =>
      apiFetch<{ expense: Expense; suggest_billing_flip: boolean }>(
        `/api/v1/organizations/${org}/expenses`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: expensesKey(org, interventionUid) })
      if (interventionUid) {
        qc.invalidateQueries({
          queryKey: interventionEventsKey(org, interventionUid),
        })
      }
    },
  })
}

export function useDeleteExpense(org: string, interventionUid?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uid: string) =>
      apiFetch<void>(`/api/v1/organizations/${org}/expenses/${uid}`, {
        method: 'DELETE',
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: expensesKey(org, interventionUid) }),
  })
}
